import React, { useState } from 'react'
import { useNoKeyboardResize } from '../hooks/useNoKeyboardResize'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authApi } from '../api'
import AppBar from '../components/AppBar'
import SubmitButton from '../components/SubmitButton'
import TermsAgreementSheet from '../components/TermsAgreementSheet'
import { rememberMarketingConsent } from '../utils/marketingConsent'

const ERROR_COLOR = 'rgba(180, 5, 8, 0.8)' // #B40508 80%

/**
 * 휴대폰번호 입력 화면. 두 진입:
 *  - 전화로 시작하기(social 없음): 번호 존재확인 → 기존이면 OTP 로그인 / 신규면 약관→OTP→가입
 *  - 소셜 무계정(?social=base64): 번호 존재확인 → 기존이면 약관스킵·OTP·연결 / 신규면 약관→OTP→가입
 * OTP 발송/검증 백엔드는 추후 — 지금은 화면·분기·형식검증 + 번호존재확인(실제).
 */
const PhoneSignup: React.FC = () => {
  const navigate = useNavigate()
  useNoKeyboardResize()
  const [searchParams] = useSearchParams()
  const socialRaw = searchParams.get('social') || ''
  const [phone, setPhone] = useState('')
  const [error, setError] = useState('')
  const [showTerms, setShowTerms] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // 010-1234-5678 형식으로 표시
  const formatPhone = (raw: string): string => {
    const d = raw.replace(/\D/g, '').slice(0, 11)
    if (d.length < 4) return d
    if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`
    return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`
  }

  const digits = phone.replace(/\D/g, '')
  const isValid = /^01[016789][0-9]{7,8}$/.test(digits)

  const handleNext = async () => {
    if (submitting) return
    if (!isValid) {
      setError('올바른 휴대폰 번호를 입력해주세요')
      return
    }
    // 두 흐름 모두 번호 존재 여부를 먼저 확인 → 약관은 '신규 번호'일 때만.
    setSubmitting(true)
    try {
      const res = await authApi.checkPhone({ phone: digits })
      const isExisting = res.available === false
      if (socialRaw) {
        if (isExisting) {
          // 소셜 + 기존 번호 = 계정 연결 → 약관 스킵, 바로 OTP (동의는 원가입 때 완료)
          navigate(`/register/phone/verify?phone=${digits}&social=${socialRaw}`)
        } else {
          // 소셜 + 신규 번호 = 새 가입 → 약관 동의 후 OTP
          setShowTerms(true)
        }
      } else if (isExisting) {
        // 전화 기존 회원 → OTP 인증 후 로그인
        navigate(`/register/phone/verify?phone=${digits}&mode=login`)
      } else {
        // 전화 신규 → 약관 동의 후 OTP
        setShowTerms(true)
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || '확인 중 오류가 발생했습니다')
    } finally {
      setSubmitting(false)
    }
  }

  // 약관 동의 완료 → 문자 인증번호(OTP) 화면으로. (발송/검증 백엔드는 추후)
  const handleAgree = (agree: { marketing: boolean }) => {
    // 계정은 두 화면 뒤에 만들어진다. 그때까지 이 값을 들고 간다
    rememberMarketingConsent(agree.marketing)
    setShowTerms(false)
    if (socialRaw) {
      // 소셜 신규: OTP 뒤에 번호 존재확인(기존이면 연결, 신규면 아이디/닉네임)
      navigate(`/register/phone/verify?phone=${digits}&social=${socialRaw}`)
    } else {
      // 전화 신규: OTP 뒤에 아이디/닉네임
      navigate(`/register/phone/verify?phone=${digits}&mode=signup`)
    }
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden pb-32px">
        {/* 뒤로가기 (앱 표준 AppBar, 타이틀 없음) */}
        <AppBar onBack={() => navigate(-1)} />

        <div className="flex-1 overflow-y-auto scrollbar-hide px-20px pt-40px">
          {/* 헤딩 */}
          <p className="font-pretendard font-bold text-22px leading-135% text-brown-80%-main">
            휴대폰 번호를 입력해주세요
          </p>

          {/* 밑줄형 입력 */}
          <div className="mt-32px">
            <input
              value={phone}
              onChange={(e) => {
                setPhone(formatPhone(e.target.value))
                if (error) setError('')
              }}
              inputMode="numeric"
              type="tel"
              placeholder="010-1234-5678"
              className="w-full bg-transparent border-none outline-none font-pretendard font-bold text-22px leading-135% placeholder:text-472410-20% py-8px"
              style={{
                color: error ? ERROR_COLOR : 'rgba(75, 54, 33, 0.8)',
              }}
            />
            <div
              className="h-2px w-full"
              style={{
                backgroundColor: error ? ERROR_COLOR : 'rgba(75, 54, 33, 0.2)',
              }}
            />

            {error && (
              <div className="flex items-center gap-8px mt-12px">
                <img
                  src="/images/signup-x.png"
                  alt=""
                  className="w-20px h-20px object-contain"
                />
                <span
                  className="font-pretendard font-bold text-15px"
                  style={{ color: ERROR_COLOR }}
                >
                  {error}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* 하단 다음으로 */}
        <div className="w-full px-20px">
          <SubmitButton
            text="다음으로"
            onSubmit={handleNext}
            onDisabled={handleNext}
            loading={submitting}
          />
        </div>
      </div>

      {/* 이용약관 동의 (휴대폰 입력 다음 단계)
          — 드래그·dim터치·뒤로가기로 닫으면 휴대폰 번호 입력 화면으로 복귀(onClose) */}
      <TermsAgreementSheet
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
        onComplete={handleAgree}
        dismissible={true}
      />
    </div>
  )
}

export default PhoneSignup
